import {
  DragDropTarget,
  DragOutcome,
  DragStartDetails,
  DragTelemetryRequest,
  IgnoredReleaseReason,
} from "../types/dragTelemetry";
import { IntersectionDetails } from "../types/dragAndDrop";
import { DragAndDropApi, isShiftLocation } from "./DragAndDropApi";

/*
 * Collects one summary per drag and sends it to the backend log when the drag ends. The browser is
 * the only place that sees a release which never becomes an edit request (the visit stays on the
 * cursor), so these events are how such releases show up in the production log.
 *
 * State is module-level: there is only ever one drag at a time, and the events come from both the
 * dragged block and the gantt wrapper, which share no React state.
 */

export type DragTelemetrySession = DragStartDetails & {
  startedAt: number;
  isButtonDownAtStart: boolean;
  pointerType: string | null;
  ignoredReasons: IgnoredReleaseReason[];
  ignoredReleases: number;
  isFinished: boolean;
  firstIgnoredAt: number | null;
  dropRequests: number;
  releaseAt: number | null;
  editDoneAt: number | null;
  statusDoneAt: number | null;
  longTasks: number;
  longTaskMs: number;
  observer: PerformanceObserver | null;
  stillDraggingTimer: ReturnType<typeof setTimeout> | null;
  pressesDuringDrag: number;
  releasesDuringDrag: number;
  pointerTravelPx: number;
  lastPointerPosition: { x: number; y: number } | null;
  lastMoveAt: number;
  stillDraggingReports: number;
  slotKey: string | null;
  zone: string | null;
  visitedZones: Set<string>;
  slotChanges: number;
  zoneChanges: number;
  revisitedZones: number;
  firstTargetAt: number | null;
  zoneTrail: string[];
};

type PointerState = {
  isDown: boolean;
  type: string | null;
};

type NavigatorWithMemory = Navigator & { deviceMemory?: number };

// Short enough to report before a user stuck with a visit on the cursor gives up and reloads;
// the button state and idle time in the report separate a stuck drag from a slow one.
export const STILL_DRAGGING_AFTER_MS = 4000;

// A drag that stays active is reported again at this interval, up to the cap (about 5 minutes).
export const STILL_DRAGGING_REPEAT_MS = 30000;
export const MAX_STILL_DRAGGING_REPORTS = 10;

// Matches the backend's cap on list fields.
const MAX_TRAIL = 20;

const EMPTY_TARGET: DragDropTarget = {
  toShiftId: null,
  beforeTaskId: null,
  afterTaskId: null,
};

let activeDrag: DragTelemetrySession | null = null;
let pointer: PointerState = { isDown: false, type: null };
let isPointerTracked = false;

const now = (): number => performance.now();

const elapsedSince = (
  start: number | null,
  end: number | null,
): number | null =>
  start !== null && end !== null ? Math.round(end - start) : null;

// A drag can start from a timer after the button is already released somewhere else, which the
// dragged block never sees, so the button state is tracked on the window.
const trackPointer = (): void => {
  if (isPointerTracked) return;
  isPointerTracked = true;
  const update =
    (isDown: boolean) =>
    (event: PointerEvent): void => {
      pointer = { isDown, type: event.pointerType };
      if (activeDrag && !activeDrag.isFinished) {
        if (isDown) activeDrag.pressesDuringDrag += 1;
        else activeDrag.releasesDuringDrag += 1;
      }
    };
  window.addEventListener("pointerdown", update(true), true);
  window.addEventListener("pointerup", update(false), true);
  window.addEventListener(
    "pointermove",
    (event: PointerEvent) => {
      const drag = activeDrag;
      if (!drag || drag.isFinished) return;
      const last = drag.lastPointerPosition;
      if (last)
        drag.pointerTravelPx += Math.hypot(
          event.clientX - last.x,
          event.clientY - last.y,
        );
      drag.lastPointerPosition = { x: event.clientX, y: event.clientY };
      drag.lastMoveAt = now();
    },
    true,
  );
  window.addEventListener("pointercancel", update(false), true);
  window.addEventListener(
    "mouseup",
    () => {
      pointer = { ...pointer, isDown: false };
    },
    true,
  );
  // A visit stuck on the cursor often ends in a reload or closed tab, which would never report.
  window.addEventListener("pagehide", () => {
    DragTelemetryService.finish("ABANDONED");
  });
};

const observeLongTasks = (
  drag: DragTelemetrySession,
): PerformanceObserver | null => {
  if (
    typeof PerformanceObserver === "undefined" ||
    !PerformanceObserver.supportedEntryTypes?.includes("longtask")
  ) {
    return null;
  }
  try {
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        drag.longTasks += 1;
        drag.longTaskMs += entry.duration;
      });
    });
    observer.observe({ type: "longtask", buffered: false });
    return observer;
  } catch {
    // Some browsers advertise longtask support but cannot observe it.
    return null;
  }
};

const buildRequest = (
  drag: DragTelemetrySession,
  outcome: DragOutcome,
  target: DragDropTarget,
  endedAt: number,
): DragTelemetryRequest => ({
  ...target,
  taskId: drag.taskId,
  fromShiftId: drag.fromShiftId,
  startTrigger: drag.trigger,
  buttonDownAtStart: drag.isButtonDownAtStart,
  mouseDownToStartMs: Math.round(drag.startedAt - drag.mouseDownAt),
  ignoredReleases: drag.ignoredReleases,
  ignoredReasons: drag.ignoredReasons,
  stuckMs: elapsedSince(drag.firstIgnoredAt, endedAt),
  outcome,
  dropRequests: drag.dropRequests,
  dragDurationMs: Math.round(endedAt - drag.startedAt),
  editRequestMs: elapsedSince(drag.releaseAt, drag.editDoneAt),
  statusRequestMs: elapsedSince(drag.editDoneAt, drag.statusDoneAt),
  releaseToRenderMs:
    outcome === "DROPPED" ? elapsedSince(drag.releaseAt, endedAt) : null,
  longTasks: drag.longTasks,
  longTaskMs: Math.round(drag.longTaskMs),
  pressesDuringDrag: drag.pressesDuringDrag,
  releasesDuringDrag: drag.releasesDuringDrag,
  pointerTravelPx: Math.round(drag.pointerTravelPx),
  idleMs: Math.round(endedAt - drag.lastMoveAt),
  isButtonDown: pointer.isDown,
  slotChanges: drag.slotChanges,
  zoneChanges: drag.zoneChanges,
  distinctZones: drag.visitedZones.size,
  revisitedZones: drag.revisitedZones,
  firstTargetMs: elapsedSince(drag.startedAt, drag.firstTargetAt),
  zoneTrail: drag.zoneTrail,
  blockHeightPx: drag.blockHeightPx,
  pixelsPerMinute: drag.pixelsPerMinute,
  pointerType: drag.pointerType,
  hardwareConcurrency: navigator.hardwareConcurrency ?? null,
  deviceMemoryGb: (navigator as NavigatorWithMemory).deviceMemory ?? null,
  userAgent: navigator.userAgent,
});

class DragTelemetryService {
  /** Timestamp for the press that may start a drag, comparable with the drag start time. */
  static notePress(pointerType: string = pointer.type ?? "mouse"): number {
    trackPointer();
    pointer = { type: pointerType, isDown: true };
    return now();
  }

  static start(details: DragStartDetails): void {
    activeDrag?.observer?.disconnect();
    if (activeDrag?.stillDraggingTimer)
      clearTimeout(activeDrag.stillDraggingTimer);
    const drag: DragTelemetrySession = {
      ...details,
      startedAt: now(),
      isButtonDownAtStart: pointer.isDown,
      pointerType: pointer.type,
      ignoredReasons: [],
      ignoredReleases: 0,
      isFinished: false,
      firstIgnoredAt: null,
      dropRequests: 0,
      releaseAt: null,
      editDoneAt: null,
      statusDoneAt: null,
      longTasks: 0,
      longTaskMs: 0,
      observer: null,
      stillDraggingTimer: null,
      pressesDuringDrag: 0,
      releasesDuringDrag: 0,
      pointerTravelPx: 0,
      lastPointerPosition: null,
      lastMoveAt: now(),
      stillDraggingReports: 0,
      slotKey: null,
      zone: null,
      visitedZones: new Set(),
      slotChanges: 0,
      zoneChanges: 0,
      revisitedZones: 0,
      firstTargetAt: null,
      zoneTrail: [],
    };
    drag.observer = observeLongTasks(drag);
    drag.stillDraggingTimer = setTimeout(
      () => DragTelemetryService.reportStillDragging(drag),
      STILL_DRAGGING_AFTER_MS,
    );
    activeDrag = drag;
  }

  /** Called whenever the gantt chart computes a drop target; counts only real changes of slot or zone. */
  static recordTarget(details: IntersectionDetails): void {
    const drag = activeDrag;
    if (!drag || drag.isFinished) return;
    const zone = isShiftLocation(details.destinationLocation)
      ? `S${details.destinationLocation.shiftId}`
      : "U";
    const slotKey = `${zone}:${details.destinationWorkTaskIdBefore}:${details.destinationWorkTaskIdAfter}`;
    if (slotKey === drag.slotKey) return;
    const at = now();
    drag.firstTargetAt ??= at;
    drag.slotKey = slotKey;
    drag.slotChanges += 1;
    if (zone === drag.zone) return;
    drag.zoneChanges += 1;
    if (drag.visitedZones.has(zone)) drag.revisitedZones += 1;
    drag.visitedZones.add(zone);
    drag.zone = zone;
    if (drag.zoneTrail.length < MAX_TRAIL)
      drag.zoneTrail.push(`${zone}_${Math.round(at - drag.startedAt)}`);
  }

  static recordIgnoredRelease(reason: IgnoredReleaseReason): void {
    if (!activeDrag) return;
    activeDrag.ignoredReleases += 1;
    // Keep the full count, but match the backend's limit on individual reasons.
    if (activeDrag.ignoredReasons.length < 20)
      activeDrag.ignoredReasons.push(reason);
    activeDrag.firstIgnoredAt ??= now();
  }

  static markDropSent(): DragTelemetrySession | null {
    if (!activeDrag) return null;
    activeDrag.dropRequests += 1;
    // Repeated clicks while a request is pending must not hide the original wait.
    activeDrag.releaseAt ??= now();
    return activeDrag;
  }

  static markEditResponse(
    drag: DragTelemetrySession | null = activeDrag,
  ): void {
    if (drag && !drag.isFinished) drag.editDoneAt ??= now();
  }

  static markStatusResponse(
    drag: DragTelemetrySession | null = activeDrag,
  ): void {
    if (drag && !drag.isFinished) drag.statusDoneAt ??= now();
  }

  /** Interim report for a drag that has not ended, so a stuck drag is logged even if it never ends. */
  static reportStillDragging(drag: DragTelemetrySession): void {
    if (drag.isFinished) return;
    const request = buildRequest(drag, "STILL_DRAGGING", EMPTY_TARGET, now());
    DragAndDropApi.sendDragTelemetry(request).catch(() => undefined);
    drag.stillDraggingReports += 1;
    if (drag.stillDraggingReports < MAX_STILL_DRAGGING_REPORTS)
      drag.stillDraggingTimer = setTimeout(
        () => DragTelemetryService.reportStillDragging(drag),
        STILL_DRAGGING_REPEAT_MS,
      );
  }

  /** Sends the summary once; later calls for the same drag (e.g. one per Escape listener) do nothing. */
  static finish(
    outcome: DragOutcome,
    target: DragDropTarget = EMPTY_TARGET,
    drag: DragTelemetrySession | null = activeDrag,
  ): void {
    if (!drag || drag.isFinished) return;
    drag.isFinished = true;
    if (activeDrag === drag) activeDrag = null;
    drag.observer?.disconnect();
    if (drag.stillDraggingTimer) clearTimeout(drag.stillDraggingTimer);
    const request = buildRequest(drag, outcome, target, now());
    // Telemetry must never disturb planning, so failures are dropped silently
    DragAndDropApi.sendDragTelemetry(request).catch(() => undefined);
  }
}

export default DragTelemetryService;
